import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  IconButton,
  InputLabel,
  Stack,
  Tooltip,
} from "@mui/material";
import { FormTextField } from "UI/FormTextField";
import { GoBack } from "UI/GoBack";
import { Spinner } from "UI/Spinner";
import { ErrorWindow } from "components/ErrorWindow";
import { ReactComponent as RSLogo } from "images/rs-logo.svg";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getPasswordConfirmValidation, passwordValidation } from "utils/index";
import { Container, Header } from "../Dashboard.style";
import { ReactComponent as HelpIcon } from "images/help-icon.svg";
import { toast } from "react-toastify";
import { toastifyConfig } from "./ReferralForm/ReferralForm.common";
import { ReactComponent as SuccessIcon } from "images/success.svg";

const defaultValues = {
  password: "",
  password_repeat: "",
};

export const PasswordChange = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const formCode = searchParams?.get("formCode");
  const backUrl = formCode ? `/embedded_form/${formCode}` : "/";

  const [newToken, setNewToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState("");
  const [reissueTokenError, setReissueTokenError] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const isFirstRenderRef = useRef(true);

  const { control, handleSubmit, watch } = useForm({
    defaultValues,
  });
  const password = useRef("");
  password.current = watch("password", "");

  const currentYear = new Date().getFullYear();

  const reissueToken = async (token) => {
    setIsChecking(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/auth/reissue-password-change-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token }),
        }
      );
      const result = await response.json();
      if (response?.status !== 200 || result?.error) {
        setReissueTokenError(
          result?.message || result?.error || "Something went wrong"
        );
        setIsChecking(false);
        return;
      }

      if (!result?.passwordGrant?.token) {
        setReissueTokenError("Something went wrong. Call support");
        setIsChecking(false);
        return;
      }

      setNewToken(result?.passwordGrant?.token);
    } catch (err) {
      setReissueTokenError(
        err?.message ?? "Something went wrong. Call support"
      );
    } finally {
      setIsChecking(false);
    }
  };

  const onSubmit = handleSubmit(async ({ password }) => {
    setIsChecking(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/auth/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token: newToken, password }),
        }
      );
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        setError(result?.message || result?.error || "Something went wrong");
        setIsChecking(false);
        return;
      }

      if (result?.dashboardAccessToken) {
        window.localStorage.setItem(`accessToken`, result.dashboardAccessToken);
        window.localStorage.setItem(`buttonTokens`, JSON.stringify([]));
        setLoading(true);
        toast(
          () => (
            <Stack direction={"row"} gap={1}>
              <SuccessIcon />
              <Stack>Password reset confirmed.</Stack>
            </Stack>
          ),
          toastifyConfig
        );
        setTimeout(() => (window.location.href = backUrl), 1500);
        return;
      }

      setError("Something went wrong. Call support");
      setIsChecking(false);
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
      setIsChecking(false);
    }
  });

  useEffect(() => {
    if (token && isFirstRenderRef.current) {
      reissueToken(token);
      isFirstRenderRef.current = false;
    }
  }, [token]);

  return (
    <Container>
      <Header>
        <RSLogo width={"103px"} />
      </Header>
      {newToken && (
        <div className="flex-1 mb-10 flex flex-col items-center justify-center gap-6">
          <div className="flex flex-col-reverse md:flex-row justify-center items-center gap-2">
            <div className="max-w-96">
              <Box
                component="form"
                onSubmit={(e) => e.preventDefault()}
                noValidate
              >
                <div
                  className={`p-4 sm:p-8 flex flex-col gap-4 rounded-xl border border-solid
                border-gray-300 shadow-lg min-w-72 sm:min-w-80`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex flex-row justify-center items-center gap-1">
                      <Link to={backUrl}>
                        <GoBack />
                      </Link>
                      <div className="text-2xl font-bold">
                        Set a New Password
                      </div>
                    </div>
                  </div>
                  <Stack
                    direction={"column"}
                    alignItems="center"
                    width="100%"
                    gap={3}
                    mt={1}
                    mb={3}
                  >
                    <Stack alignItems={"flex-start"}>
                      <div className="flex flex-row justify-start items-center gap-1">
                        <InputLabel htmlFor="password">New Password</InputLabel>
                        <Tooltip title="Password must contain at least 8 characters, one lowercase character, one uppercase character, one number, and one special character (e.g “!@#$%^&*)">
                          <HelpIcon width={18} />
                        </Tooltip>
                      </div>
                      <FormTextField
                        id="password"
                        name="password"
                        control={control}
                        rules={passwordValidation}
                        type={isPasswordVisible ? "text" : "password"}
                        inputProps={{
                          autoComplete: "new-password",
                        }}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              color="info"
                              onClick={() =>
                                setIsPasswordVisible(!isPasswordVisible)
                              }
                            >
                              {isPasswordVisible ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          ),
                          autoComplete: "new-password",
                        }}
                      />
                    </Stack>
                    <Stack alignItems={"flex-start"}>
                      <InputLabel htmlFor="password_repeat">
                        {"Confirm New Password"}
                      </InputLabel>
                      <FormTextField
                        id="password_repeat"
                        name="password_repeat"
                        control={control}
                        rules={getPasswordConfirmValidation(password.current)}
                        type={isPasswordVisible ? "text" : "password"}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              color="info"
                              onClick={() =>
                                setIsPasswordVisible(!isPasswordVisible)
                              }
                            >
                              {isPasswordVisible ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          ),
                          autoComplete: "new-password",
                        }}
                      />
                    </Stack>
                  </Stack>
                  <Button
                    disabled={loading || isChecking}
                    variant="contained"
                    onClick={onSubmit}
                  >
                    Continue
                  </Button>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
      {!newToken && reissueTokenError && (
        <div className="flex-1 mb-10 flex flex-col items-center justify-center gap-6">
          <div className="flex flex-col-reverse md:flex-row justify-center items-center gap-2">
            <div
              className={`p-4 sm:p-8 flex flex-col items-center gap-8 rounded-xl border border-solid
                border-gray-300 shadow-lg min-w-72 sm:min-w-80`}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="text-2xl font-bold">Link Expired</div>
                <div className="text-xs sm:text-sm text-center">
                  {reissueTokenError}
                </div>
              </div>
              <Link to={"/forgot_password"}>
                <Button variant="contained">Resend Link</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
      <div className="mb-4 text-gray-500 text-sm flex-grow-0 flex-shrink-0 text-center">
        Copyright © {currentYear} Referral Stream. All rights reserved.
      </div>
      {error && <ErrorWindow error={error} handleClose={() => setError("")} />}
      {(loading || isChecking) && <Spinner />}
    </Container>
  );
};
